import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Database, GripVertical, Plus, RefreshCw, RotateCcw, Save, Search } from "lucide-react";

import api from "../api";
import { clearMasterDataCache } from "../../utils/useMasterData";

const MODULES = ["ALL", "INVENTORY", "SALES", "PRODUCTION", "QC", "HR", "ACCOUNTS"];
const COMPLEX_GROUPS = [
  { group_key: "DESIGN_CATEGORY_MASTER", module: "PRODUCTION", active_count: 0, total_count: 0, kind: "complex", endpoint: "/admin/categories", title: "Design Categories" },
  { group_key: "DESIGN_FAMILY_MASTER", module: "PRODUCTION", active_count: 0, total_count: 0, kind: "complex", endpoint: "/admin/families", title: "Design Families" },
];

const styles = {
  wrap: { display: "grid", gap: 16 },
  shell: { display: "grid", gridTemplateColumns: "320px 1fr", gap: 16 },
  card: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, boxShadow: "0 10px 22px rgba(15,23,42,.06)" },
  head: { padding: 18, borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  btn: { display: "inline-flex", alignItems: "center", gap: 8, borderRadius: 10, border: "1px solid #cbd5e1", padding: "10px 14px", background: "#fff", color: "#0f172a", fontWeight: 700, cursor: "pointer" },
  primaryBtn: { display: "inline-flex", alignItems: "center", gap: 8, borderRadius: 10, border: "none", padding: "10px 14px", background: "#4f46e5", color: "#fff", fontWeight: 700, cursor: "pointer" },
  input: { width: "100%", borderRadius: 10, border: "1px solid #cbd5e1", padding: "10px 12px", background: "#fff" },
};

const emptyRow = () => ({
  id: `new-${Math.random().toString(36).slice(2)}`,
  value: "",
  label: "",
  is_default: false,
  active: true,
  isNew: true,
});
const emptyComplexRow = () => ({
  id: `new-${Math.random().toString(36).slice(2)}`,
  name: "",
  code: "",
  description: "",
  active: true,
  isNew: true,
});

export default function MasterDataManagerView() {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedModule, setSelectedModule] = useState("ALL");
  const [groupSearch, setGroupSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [complexRows, setComplexRows] = useState([]);

  const selectedComplexGroup = useMemo(
    () => COMPLEX_GROUPS.find((group) => group.group_key === selectedGroup) || null,
    [selectedGroup]
  );

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const res = await api.get("/master-data/groups");
      const data = res.data?.data || [];
      const complexStats = await Promise.all(
        COMPLEX_GROUPS.map(async (group) => {
          try {
            const rowsRes = await api.get(group.endpoint);
            const items = rowsRes.data?.data || [];
            return { ...group, active_count: items.filter((item) => item.is_active !== false).length, total_count: items.length };
          } catch {
            return group;
          }
        })
      );
      const merged = [...data, ...complexStats];
      setGroups(merged);
      if (!selectedGroup && merged[0]?.group_key) {
        setSelectedGroup(merged[0].group_key);
      }
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to load master-data groups." });
    } finally {
      setLoadingGroups(false);
    }
  }, [selectedGroup]);

  const loadGroupRows = useCallback(async (groupKey) => {
    if (!groupKey) return;
    setLoadingRows(true);
    try {
      const complexGroup = COMPLEX_GROUPS.find((group) => group.group_key === groupKey);
      if (complexGroup) {
        const res = await api.get(complexGroup.endpoint);
        const data = (res.data?.data || []).map((item) => ({
          id: item.id,
          name: item.name || "",
          code: item.code || "",
          description: item.description || "",
          active: Boolean(item.is_active),
        }));
        setComplexRows(data);
        setRows([]);
        setMessage({ type: "", text: "" });
        return;
      }
      const res = await api.get(`/master-data/groups/${groupKey}`);
      const data = (res.data?.data || []).map((item) => ({
        id: item.id,
        value: item.option_value,
        label: item.option_label,
        is_default: Boolean(item.is_default),
        active: Boolean(item.is_active),
        module: item.module,
      }));
      setRows(data);
      setComplexRows([]);
      setMessage({ type: "", text: "" });
    } catch (err) {
      setRows([]);
      setComplexRows([]);
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to load options." });
    } finally {
      setLoadingRows(false);
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { if (selectedGroup) loadGroupRows(selectedGroup); }, [loadGroupRows, selectedGroup]);

  const filteredGroups = useMemo(() => groups.filter((group) => {
    const matchesModule = selectedModule === "ALL" || group.module === selectedModule;
    const q = groupSearch.trim().toLowerCase();
    const matchesQuery = !q || group.group_key.toLowerCase().includes(q);
    return matchesModule && matchesQuery;
  }), [groupSearch, groups, selectedModule]);

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((row) => {
      if (row.id !== id) return row;
      return { ...row, ...patch };
    }).map((row) => (patch?.is_default && row.id !== id ? { ...row, is_default: false } : row)));
  };

  const addOption = () => setRows((prev) => [...prev, emptyRow()]);

  const addComplexRow = () => {
    setComplexRows((prev) => [...prev, emptyComplexRow()]);
  };

  const updateComplexRow = (id, patch) => {
    setComplexRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const saveComplexRow = async (row) => {
    if (!selectedComplexGroup || !row.name?.trim() || !row.code?.trim()) return;
    setSaving(true);
    try {
      if (String(row.id).startsWith("new-")) {
        await api.post(selectedComplexGroup.endpoint, {
          name: row.name.trim(),
          code: row.code.trim(),
          description: row.description?.trim() || "",
        });
      } else {
        await api.put(`${selectedComplexGroup.endpoint}/${row.id}`, {
          name: row.name.trim(),
          code: row.code.trim(),
          description: row.description?.trim() || "",
          is_active: row.active,
        });
      }
      await Promise.all([loadGroups(), loadGroupRows(selectedGroup)]);
      setMessage({ type: "success", text: `${selectedComplexGroup.title} saved.` });
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to save record." });
    } finally {
      setSaving(false);
    }
  };

  const toggleComplexActive = async (row) => {
    if (!selectedComplexGroup) return;
    if (String(row.id).startsWith("new-")) {
      updateComplexRow(row.id, { active: !row.active });
      return;
    }
    setSaving(true);
    try {
      await api.put(`${selectedComplexGroup.endpoint}/${row.id}`, { is_active: !row.active });
      await Promise.all([loadGroups(), loadGroupRows(selectedGroup)]);
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to update record." });
    } finally {
      setSaving(false);
    }
  };

  const saveRow = async (row) => {
    if (!selectedGroup || !row.value.trim() || !row.label.trim()) return;
    setSaving(true);
    try {
      if (String(row.id).startsWith("new-")) {
        await api.post("/master-data/options", {
          group_key: selectedGroup,
          option_value: row.value.trim(),
          option_label: row.label.trim(),
          is_default: row.is_default,
          display_order: rows.findIndex((item) => item.id === row.id) + 1,
        });
      } else {
        await api.put(`/master-data/options/${row.id}`, {
          option_label: row.label.trim(),
          is_active: row.active,
          is_default: row.is_default,
          display_order: rows.findIndex((item) => item.id === row.id) + 1,
        });
      }
      clearMasterDataCache(selectedGroup);
      await Promise.all([loadGroups(), loadGroupRows(selectedGroup)]);
      setMessage({ type: "success", text: "Option saved." });
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to save option." });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    if (String(row.id).startsWith("new-")) {
      updateRow(row.id, { active: !row.active });
      return;
    }
    setSaving(true);
    try {
      await api.put(`/master-data/options/${row.id}`, { is_active: !row.active });
      clearMasterDataCache(selectedGroup);
      await loadGroupRows(selectedGroup);
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to update option." });
    } finally {
      setSaving(false);
    }
  };

  const reorderRows = async (nextRows) => {
    setRows(nextRows);
    const payload = nextRows.filter((row) => !String(row.id).startsWith("new-")).map((row, index) => ({ id: row.id, display_order: index + 1 }));
    if (!payload.length) return;
    try {
      await api.post("/master-data/reorder", { group_key: selectedGroup, order: payload });
      clearMasterDataCache(selectedGroup);
      await loadGroups();
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to reorder options." });
    }
  };

  const onDropRow = async (targetId) => {
    if (!draggedId || draggedId === targetId) return;
    const current = [...rows];
    const from = current.findIndex((row) => row.id === draggedId);
    const to = current.findIndex((row) => row.id === targetId);
    if (from < 0 || to < 0) return;
    const [moved] = current.splice(from, 1);
    current.splice(to, 0, moved);
    setDraggedId(null);
    await reorderRows(current);
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset all default master-data groups for this organization?")) return;
    setSaving(true);
    try {
      await api.post("/master-data/seed-defaults");
      clearMasterDataCache();
      await Promise.all([loadGroups(), loadGroupRows(selectedGroup)]);
      setMessage({ type: "success", text: "Defaults re-seeded successfully." });
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Failed to reset defaults." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <div style={styles.head}>
          <div>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#0f172a" }}>Master Data Manager</h2>
            <p style={{ margin: "6px 0 0", color: "#64748b" }}>Manage all simple dropdown groups from one place.</p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={styles.btn} onClick={loadGroups}><RefreshCw size={16} /> Refresh</button>
            <button style={styles.primaryBtn} onClick={resetDefaults} disabled={saving}><RotateCcw size={16} /> Reset All Defaults</button>
          </div>
        </div>
        {message.text ? (
          <div style={{ margin: 18, borderRadius: 12, padding: "12px 14px", background: message.type === "error" ? "#fef2f2" : "#ecfdf5", color: message.type === "error" ? "#b91c1c" : "#166534", border: `1px solid ${message.type === "error" ? "#fecaca" : "#bbf7d0"}` }}>
            {message.text}
          </div>
        ) : null}
        <div style={styles.shell}>
          <div style={{ ...styles.card, margin: 16 }}>
            <div style={{ padding: 16, borderBottom: "1px solid #e2e8f0", display: "grid", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Search size={16} color="#64748b" />
                <input value={groupSearch} onChange={(e) => setGroupSearch(e.target.value)} placeholder="Search group" style={styles.input} />
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {MODULES.map((module) => (
                  <button key={module} style={{ ...styles.btn, background: selectedModule === module ? "#eef2ff" : "#fff", borderColor: selectedModule === module ? "#818cf8" : "#cbd5e1", color: selectedModule === module ? "#4338ca" : "#0f172a", padding: "8px 10px" }} onClick={() => setSelectedModule(module)}>
                    {module}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ maxHeight: 680, overflow: "auto", padding: 8 }}>
              {loadingGroups ? <div style={{ padding: 16, color: "#64748b" }}>Loading groups...</div> : filteredGroups.map((group) => (
                <button key={group.group_key} onClick={() => setSelectedGroup(group.group_key)} style={{ width: "100%", border: "1px solid #e2e8f0", borderRadius: 12, background: selectedGroup === group.group_key ? "#eef2ff" : "#fff", padding: 12, textAlign: "left", marginBottom: 8, cursor: "pointer" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>{group.group_key}</div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>{group.module}</div>
                    </div>
                    <span style={{ borderRadius: 999, background: "#e2e8f0", padding: "4px 10px", fontSize: 12, fontWeight: 700 }}>{group.active_count}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div style={{ ...styles.card, margin: 16, overflow: "hidden" }}>
            <div style={styles.head}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Database size={18} color="#4338ca" />
                <div>
                  <div style={{ fontWeight: 800, color: "#0f172a" }}>{selectedComplexGroup?.title || selectedGroup || "Select a group"}</div>
                  <div style={{ color: "#64748b", fontSize: 13 }}>
                    {selectedComplexGroup ? "Design master dropdown sources managed with dedicated CRUD." : "Inline edit, default toggle, active state, and drag reorder"}
                  </div>
                </div>
              </div>
              <button style={styles.primaryBtn} onClick={selectedComplexGroup ? addComplexRow : addOption}>
                <Plus size={16} /> {selectedComplexGroup ? "Add Record" : "Add Option"}
              </button>
            </div>
            {selectedComplexGroup ? (
              <div style={{ display: "grid", gap: 16, padding: 16 }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ background: "#f8fafc", color: "#64748b", fontSize: 12, textTransform: "uppercase" }}>
                      <tr>
                        <th style={{ padding: 12, textAlign: "left" }}>#</th>
                        <th style={{ padding: 12, textAlign: "left" }}>Name</th>
                        <th style={{ padding: 12, textAlign: "left" }}>Code</th>
                        <th style={{ padding: 12, textAlign: "left" }}>Description</th>
                        <th style={{ padding: 12, textAlign: "center" }}>Active</th>
                        <th style={{ padding: 12, textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingRows ? (
                        <tr><td colSpan="6" style={{ padding: 18, color: "#64748b" }}>Loading records...</td></tr>
                      ) : complexRows.length === 0 ? (
                        <tr><td colSpan="6" style={{ padding: 18, color: "#64748b" }}>No records yet. Click "Add Record" to create one here or use seed defaults in Production Setup.</td></tr>
                      ) : complexRows.map((row, index) => (
                        <tr key={row.id} style={{ borderTop: "1px solid #e2e8f0" }}>
                          <td style={{ padding: 12 }}>{index + 1}</td>
                          <td style={{ padding: 12 }}><input value={row.name} onChange={(e) => updateComplexRow(row.id, { name: e.target.value })} style={styles.input} /></td>
                          <td style={{ padding: 12 }}><input value={row.code} onChange={(e) => updateComplexRow(row.id, { code: e.target.value })} style={styles.input} /></td>
                          <td style={{ padding: 12 }}><input value={row.description} onChange={(e) => updateComplexRow(row.id, { description: e.target.value })} style={styles.input} /></td>
                          <td style={{ padding: 12, textAlign: "center" }}><input type="checkbox" checked={row.active} onChange={() => toggleComplexActive(row)} /></td>
                          <td style={{ padding: 12, textAlign: "right" }}><button style={styles.btn} onClick={() => saveComplexRow(row)} disabled={saving || !row.name.trim() || !row.code.trim()}><Save size={14} /> Save</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead style={{ background: "#f8fafc", color: "#64748b", fontSize: 12, textTransform: "uppercase" }}>
                    <tr>
                      <th style={{ padding: 12, textAlign: "left" }}>#</th>
                      <th style={{ padding: 12, textAlign: "left" }}>Value</th>
                      <th style={{ padding: 12, textAlign: "left" }}>Label</th>
                      <th style={{ padding: 12, textAlign: "center" }}>Default</th>
                      <th style={{ padding: 12, textAlign: "center" }}>Active</th>
                      <th style={{ padding: 12, textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingRows ? (
                      <tr><td colSpan="6" style={{ padding: 18, color: "#64748b" }}>Loading options...</td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan="6" style={{ padding: 18, color: "#64748b" }}>No options yet. Click "Add Option" or reset defaults.</td></tr>
                    ) : rows.map((row, index) => (
                      <tr key={row.id} draggable onDragStart={() => setDraggedId(row.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDropRow(row.id)} style={{ borderTop: "1px solid #e2e8f0", background: draggedId === row.id ? "#f8fafc" : "#fff" }}>
                        <td style={{ padding: 12 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <GripVertical size={15} color="#94a3b8" />
                            <span>{index + 1}</span>
                          </div>
                        </td>
                        <td style={{ padding: 12 }}><input value={row.value} disabled={!String(row.id).startsWith("new-")} onChange={(e) => updateRow(row.id, { value: e.target.value })} style={styles.input} /></td>
                        <td style={{ padding: 12 }}><input value={row.label} onChange={(e) => updateRow(row.id, { label: e.target.value })} style={styles.input} /></td>
                        <td style={{ padding: 12, textAlign: "center" }}><input type="radio" checked={row.is_default} onChange={() => updateRow(row.id, { is_default: true })} /></td>
                        <td style={{ padding: 12, textAlign: "center" }}><input type="checkbox" checked={row.active} onChange={() => toggleActive(row)} /></td>
                        <td style={{ padding: 12, textAlign: "right" }}><button style={styles.btn} onClick={() => saveRow(row)} disabled={saving || !row.value.trim() || !row.label.trim()}><Save size={14} /> Save</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 980px) { .master-data-shell { grid-template-columns: 1fr; } }`}</style>
    </div>
  );
}
